import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import type { EligibilityRequest } from "../../../types";

import { getEligibility } from "../../../api/eligibility";
import { useSalesContext } from "../../../sales-context/SalesContext";
import { useCallDetailsStore, useProductOfferStore, useVerifiedCustomerStore } from "../../../stores";
import { parsePhoneNumberAttributes } from "../../product-sale-flow/utils/parsePhoneNumberAttributes";

const QUERY_KEY = "sales-eligibility";
const EXPERT_UNIVERSITY_SUFFIX = "test-training";

export { QUERY_KEY };

export function useEligibility(isEnabled = true) {
    const {
        sessionId,
        partner,
        logger,
        analytics,
        taskType,
        taskData,
        apiClients,
        expertLocation,
        onEligibilityResult,
        features,
    } = useSalesContext();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const hasProducts = useProductOfferStore((state) => (state.getProducts(sessionId)?.length ?? 0) > 0);

    const callDetails = useCallDetailsStore((state) => state.callDetails);
    const isCustomerInProtectedState = callDetails.PROTECTED_SALES === "1";
    const isExpertUniversitySimulatedVoiceTask = sessionId.includes(EXPERT_UNIVERSITY_SUFFIX);

    const {
        data: eligibilityResult,
        error,
        isError,
        isLoading,
    } = useQuery({
        queryKey: [
            QUERY_KEY,
            sessionId,
            customer?.mdn,
            customer?.accountNumber,
            // NOTE: email is intentionally NOT in the queryKey. Adding it (PR #6121,
            // ported from legacy PR #6037) caused useEligibility to re-fetch when
            // setCustomer wrote email, racing useCollectCustomerInfo's call inside
            // pDebounce(eligibility, 500ms). The trailing-edge fire used
            // useEligibility's no-firstName/lastName body, which sales-domain
            // Object.assign'd over the session's name fields, breaking smart-link
            // generation. See RDESQ-2032 (legacy fix shipped in PR #6178).
            customer?.currentSubscriptions,
        ],
        queryFn: async () => {
            logger.info("Checking eligibility");
            return getEligibility(
                apiClients.sales,
                logger,
                analytics,
                {
                    asurionCallId: taskType.isVoice ? taskData.asurionCallId : undefined,
                    partner,
                    sessionId,
                    customerInteractionId: taskData.customerInteractionId,
                    reservationSid: taskData.reservationSid ?? sessionId,
                    ...(isExpertUniversitySimulatedVoiceTask
                        ? { customerMdn: taskData.mdn }
                        : { ...parsePhoneNumberAttributes(customer?.mdn) }),
                    address: customer?.address,
                    customer: {
                        firstName: customer?.firstName,
                        lastName: customer?.lastName,
                        email: customer?.email,
                        accountNumber: customer?.accountNumber,
                    },
                    eligibilityAttributes: {
                        currentPlan: customer?.productName,
                        isInternationalCaller: customer?.metadata?.isInternationalCaller,
                        chatSourceApp: taskType.isChat ? taskData.chatSourceApp : undefined,
                        currentSubscriptions: customer?.currentSubscriptions,
                        expertLocation,
                        isCustomerInProtectedState,
                        client: callDetails.CLIENT,
                    },
                    salesChannel: taskData.salesChannel as EligibilityRequest["salesChannel"],
                },
                features.isUsingMovedDiscoveryAndEngagementQuestions,
            );
        },
        enabled: isEnabled && !!customer?.mdn && !hasProducts,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: 1,
    });

    useEffect(() => {
        if (eligibilityResult) {
            const store = useProductOfferStore.getState();
            store.updateProducts(sessionId, eligibilityResult.productConfigs, eligibilityResult.displayCues);
            if (eligibilityResult.meta) {
                store.updateEligibilityMetaBySessionId(sessionId, eligibilityResult.meta);
            }
            onEligibilityResult?.(eligibilityResult.productConfigs);
        }
    }, [eligibilityResult, sessionId, onEligibilityResult]);

    return { eligibilityResult, error, isError, isLoading };
}

export function clearEligibilityQueryCache(
    queryClient: { removeQueries: (opts: { predicate: (query: { queryKey: readonly unknown[] }) => boolean }) => void },
    sessionId: string,
) {
    queryClient.removeQueries({
        predicate: (query) => query.queryKey.includes(QUERY_KEY) && query.queryKey.includes(sessionId),
    });
}
